/**
 * Date helpers for values coming out of the QBO mirror (`txn_date`,
 * `due_date`, contract dates, …). Those columns are CALENDAR DATES serialized
 * as plain `YYYY-MM-DD` strings — they have no time or timezone. Parsing one
 * with `new Date('2026-07-01')` yields midnight UTC, which any US timezone
 * renders as the PREVIOUS day ("Jun 30" for a July 1 invoice — the Dead Fish
 * bug, 2026-07-09). The server pdf-lib renderers already format these in UTC;
 * these helpers make the web UI agree with the printed documents.
 *
 * Rule: date-only strings are anchored at UTC midnight and FORMATTED in UTC,
 * so the calendar date always renders as itself. Anything with a time
 * component is a real instant and renders in the viewer's local time.
 */

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True when the value is a bare calendar date (YYYY-MM-DD, no time part). */
export function isDateOnly(value: string): boolean {
  return DATE_ONLY_RE.test(value.trim());
}

/**
 * Parse a mirror date. Date-only strings anchor at UTC midnight; anything
 * else goes through the normal Date parser. Returns null when unparseable.
 */
export function parseMirrorDate(value: string): Date | null {
  const s = value.trim();
  if (DATE_ONLY_RE.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(Date.UTC(y!, m! - 1, d!));
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Format a mirror date for display. Date-only values format in UTC (never
 * shifting a day); timestamps format in the viewer's local time. Falls back
 * to the raw string when unparseable.
 */
export function fmtMirrorDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  if (!value) return '—';
  const d = parseMirrorDate(value);
  if (!d) return value;
  const opts = isDateOnly(value) ? { ...options, timeZone: 'UTC' } : options;
  try {
    return d.toLocaleDateString('en-US', opts);
  } catch {
    return value;
  }
}
